import type { Session } from "./client";

const STORAGE_KEY = "comebye.session";

/**
 * A non-secret display cache of exactly what `login()` already returned to the
 * client: userId/displayName/role. This is NOT a credential and never a token —
 * the real session lives entirely in the server-set httpOnly cookie, which JS
 * never reads or writes. `sessionStorage` (not `localStorage`) so it never
 * outlives the tab.
 *
 * It exists only because the real control plane has no `GET /session` (or
 * `/me`) endpoint yet to recover "who is this" after a reload purely from the
 * cookie (see AuthContext.tsx) — so a reload optimistically restores the last
 * known profile rather than forcing a fresh sign-in every time. It is never
 * authoritative: the server enforces authentication/authorization on every
 * request regardless of what this cache says, and the first 401 any
 * authenticated call gets clears it (via authEvents.onUnauthorized).
 */
export function readCachedSession(): Session | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      typeof parsed.userId !== "string" ||
      typeof parsed.displayName !== "string" ||
      typeof parsed.role !== "string"
    ) {
      return null;
    }
    return { userId: parsed.userId, displayName: parsed.displayName, role: parsed.role };
  } catch {
    return null;
  }
}

export function writeCachedSession(session: Session): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Non-fatal — sign-in still works for this tab; the operator just re-hydrates
    // less gracefully (a fresh /login) on their next reload.
  }
}

export function clearCachedSession(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal.
  }
}
